import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CONFIG from '../config';

// ====================================================================
// DiscountingPage                                      v22  Stage S4
// --------------------------------------------------------------------
// What changed:
//
//  B11  Forward contracts are now chosen by BANK. Until a bank is
//       picked the list is empty and says why. Changing the bank
//       reloads the list — it used to load once when the invoice was
//       chosen and then never change, which is how forwards from every
//       bank ended up on screen.
//
//  B13  The server re-checks every forward and every PCFC loan on the
//       way in. This page simply shows what it says.
//
//  B14  An invoice with no currency now explains itself instead of
//       showing an empty list with no reason.
//
//  B16  Negative amounts are refused.
//
//  B10  Error pop-ups show the real reason from the server, not
//       "please check all fields".
//
//  EEFC A "Keep in EEFC" bucket on FC (EBR) discounting. The balance
//       rule becomes:
//
//           Spot + Forward + PCFC + EEFC  =  FC discounted
//
//       The rate shown is worked out on the CONVERTED part only — the
//       parked money has not been converted, so it has no rate yet and
//       including it would make the average wrong. The parked amount
//       is shown separately. Its profit or loss appears later, on the
//       EEFC ledger, when the money finally leaves.
// ====================================================================

const fmt = (n, dp = 2) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp });

const fmt0 = (n) =>
  n === null || n === undefined || n === '' || isNaN(Number(n))
    ? '—'
    : Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 });

const num = (v) => {
  const n = parseFloat(v);
  return isNaN(n) ? 0 : n;
};

const ccyCode = (c) => (c && c.length === 6 ? c.slice(0, 3) : (c || ''));
const pairLabel = (c) =>
  c && c.length === 6 ? `${c.slice(0, 3)}/${c.slice(3)}` : (c || '—');

// B10 — show what the server actually said.
const serverMessage = (err, fallback) => {
  const d = err?.response?.data;
  if (typeof d === 'string' && d.trim()) return d;
  if (d && typeof d.title === 'string') return d.title;
  if (d && typeof d.message === 'string') return d.message;
  return fallback;
};

// The forward endpoints return { forwards, reason }. Older builds
// returned a bare array, so both shapes are accepted.
const readForwards = (data) => {
  if (Array.isArray(data)) return { list: data, reason: null };
  return { list: data?.forwards || [], reason: data?.reason || null };
};

const emptyForm = {
  customerCode: '', invoiceId: '', discountingDate: '',
  bank: '', fcAmountDiscounted: '', discountingType: 'INR',
  spotFc: '', spotRate: '', pcfcFc: '', pcfcRate: '',
  drawdownRate: '', loanDueDate: '', notes: '',
  eefcFc: '', eefcAccountId: '', benchmarkRate: ''
};

const noteStyle = { fontSize: '12px', color: '#64748B' };

const warnBox = {
  background: '#FEF9C3', border: '1px solid #FDE68A',
  borderRadius: '6px', padding: '8px 12px',
  fontSize: '12px', color: '#92400E', marginBottom: '10px'
};

const infoBox = {
  background: '#F0F9FF', border: '1px solid #BAE6FD',
  borderRadius: '6px', padding: '10px 14px',
  fontSize: '13px', marginBottom: '14px'
};

const smallInput = {
  width: '110px', padding: '4px 8px',
  border: '1px solid #CBD5E1',
  borderRadius: '4px', fontSize: '13px'
};

function DiscountingPage({ token }) {
  const auth = { headers: { authorization: `Bearer ${token}` } };

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [showEditWarning, setShowEditWarning] = useState(false);
  const [pendingEditRecord, setPendingEditRecord] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [banks, setBanks] = useState([]);
  const [openInvoices, setOpenInvoices] = useState([]);

  const [availableForwards, setAvailableForwards] = useState([]);
  const [forwardReason, setForwardReason] = useState(null);
  const [forwardAllocations, setForwardAllocations] = useState([]);

  const [availablePcfc, setAvailablePcfc] = useState([]);
  const [pcfcAllocations, setPcfcAllocations] = useState([]);

  // EEFC — which ledgers exist for this bank and currency, and the
  // market rate that will be recorded as the parcel's benchmark.
  const [eefcContext, setEefcContext] = useState(null);
  const [creatingLedger, setCreatingLedger] = useState(false);

  const [filterStatus, setFilterStatus] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    loadRecords();
    loadCustomers();
    loadBanks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ------------------------------------------------------------------
  // loaders
  // ------------------------------------------------------------------
  const loadRecords = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${CONFIG.API}/api/bills/discounting`, auth);
      setRecords(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/customers`, auth);
      setCustomers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadBanks = async () => {
    try {
      const res = await axios.get(`${CONFIG.API}/api/bills/banks`, auth);
      setBanks(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const loadOpenInvoices = async (customerCode) => {
    if (!customerCode) { setOpenInvoices([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/open-invoices/${customerCode}`, auth);
      setOpenInvoices(res.data);
    } catch (err) {
      console.error(err);
      setOpenInvoices([]);
    }
  };

  // B11 — the bank is part of the question now. No bank, no forwards.
  const loadAvailableForwards = async (invoiceId, type, date, bank) => {
    if (!invoiceId) {
      setAvailableForwards([]);
      setForwardReason(null);
      return;
    }
    if (!bank) {
      setAvailableForwards([]);
      setForwardReason(
        'Choose the bank first. A forward contract can only be used at its own bank.');
      return;
    }
    try {
      const url = type === 'INR'
        ? `${CONFIG.API}/api/bills/discounting/available-forwards/${invoiceId}`
        : `${CONFIG.API}/api/bills/discounting/available-forwards-fc/${invoiceId}/${date}`;

      if (type !== 'INR' && !date) {
        setAvailableForwards([]);
        setForwardReason('Enter the discounting date first — a forward\'s window has to cover it.');
        return;
      }

      const res = await axios.get(url, {
        ...auth,
        params: { bank }
      });
      const { list, reason } = readForwards(res.data);
      setAvailableForwards(list);
      setForwardReason(reason);
    } catch (err) {
      console.error(err);
      setAvailableForwards([]);
      setForwardReason(serverMessage(err, 'Could not load forward contracts.'));
    }
  };

  const loadAvailablePcfc = async (invoiceId, bank) => {
    if (!invoiceId || !bank) { setAvailablePcfc([]); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/available-pcfc/${invoiceId}`,
        { ...auth, params: { bank } });
      setAvailablePcfc(res.data);
    } catch (err) {
      console.error(err);
      setAvailablePcfc([]);
    }
  };

  const loadEefcContext = async (invoiceId, bank, date) => {
    if (!invoiceId || !bank) { setEefcContext(null); return; }
    try {
      const res = await axios.get(
        `${CONFIG.API}/api/bills/discounting/eefc-context/${invoiceId}`,
        { ...auth, params: { bank, date: date || '' } });
      setEefcContext(res.data);
    } catch (err) {
      console.error(err);
      setEefcContext(null);
    }
  };

  // Everything that depends on invoice + bank + type + date, in one
  // place, so no combination can be missed.
  const refreshLists = async (next) => {
    const { invoiceId, discountingType, discountingDate, bank } = next;

    await loadAvailableForwards(invoiceId, discountingType, discountingDate, bank);

    if (discountingType === 'FC') {
      await loadAvailablePcfc(invoiceId, bank);
      await loadEefcContext(invoiceId, bank, discountingDate);
    } else {
      setAvailablePcfc([]);
      setPcfcAllocations([]);
      setEefcContext(null);
    }
  };

  // ------------------------------------------------------------------
  // form handling
  // ------------------------------------------------------------------
  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = { ...form, [name]: value };

    if (name === 'customerCode') {
      next = { ...next, invoiceId: '', eefcFc: '', eefcAccountId: '' };
      setForm(next);
      loadOpenInvoices(value);
      setAvailableForwards([]);
      setForwardReason(null);
      setForwardAllocations([]);
      setAvailablePcfc([]);
      setPcfcAllocations([]);
      setEefcContext(null);
      return;
    }

    if (name === 'discountingType') {
      // Switching type throws every allocation away — the buckets and
      // the matching rules are different on each side.
      next = {
        ...next,
        spotFc: '', spotRate: '',
        eefcFc: '', eefcAccountId: '', benchmarkRate: ''
      };
      setForm(next);
      setForwardAllocations([]);
      setPcfcAllocations([]);
      refreshLists(next);
      return;
    }

    if (name === 'invoiceId') {
      next = { ...next, eefcFc: '', eefcAccountId: '' };
      setForm(next);
      setForwardAllocations([]);
      setPcfcAllocations([]);
      refreshLists(next);
      return;
    }

    if (name === 'bank') {
      // B11 — this is the reload that was missing altogether.
      setForm(next);
      setForwardAllocations([]);
      setPcfcAllocations([]);
      refreshLists(next);
      return;
    }

    if (name === 'discountingDate') {
      setForm(next);
      // The FC forward window is matched on this date, so the list moves.
      if (next.discountingType === 'FC') {
        setForwardAllocations([]);
        refreshLists(next);
      }
      return;
    }

    setForm(next);
  };

  const handleForwardAllocationChange = (forwardId, value) => {
    const existing = forwardAllocations.find(a => a.forwardId === forwardId);
    if (existing) {
      setForwardAllocations(forwardAllocations.map(a =>
        a.forwardId === forwardId ? { ...a, fcAllocated: value } : a));
    } else {
      const fwd = availableForwards.find(f => f.forwardId === forwardId);
      setForwardAllocations([...forwardAllocations, {
        forwardId,
        fcAllocated: value,
        forwardRate: fwd?.forwardRate || 0
      }]);
    }
  };

  const handlePcfcAllocationChange = (pcfcId, value) => {
    const existing = pcfcAllocations.find(a => a.pcfcId === pcfcId);
    if (existing) {
      setPcfcAllocations(pcfcAllocations.map(a =>
        a.pcfcId === pcfcId ? { ...a, fcAllocated: value } : a));
    } else {
      setPcfcAllocations([...pcfcAllocations, { pcfcId, fcAllocated: value }]);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setForwardAllocations([]);
    setAvailableForwards([]);
    setForwardReason(null);
    setAvailablePcfc([]);
    setPcfcAllocations([]);
    setEefcContext(null);
    setOpenInvoices([]);
    setEditingRecord(null);
    setShowForm(false);
  };

  // ------------------------------------------------------------------
  // editing
  // ------------------------------------------------------------------
  const handleEditClick = (record) => {
    setPendingEditRecord(record);
    setShowEditWarning(true);
  };

  const handleEditConfirm = async () => {
    const r = pendingEditRecord;
    setShowEditWarning(false);
    setPendingEditRecord(null);
    if (!r) return;

    const ddate = String(r.discountingDate || '').slice(0, 10);
    const type  = r.discountingType || 'INR';

    try {
      // The allocation detail first — it also says whether the entry is
      // locked because its EEFC money has been spent.
      const det = await axios.get(
        `${CONFIG.API}/api/bills/discounting/${r.discountingId}/detail`, auth);

      if (det.data.editLocked) {
        alert(det.data.editLockReason ||
          'This entry can no longer be edited because the money it parked in EEFC '
        + 'has already been used.');
        return;
      }

      const detFwd  = det.data.forwardAllocations || [];
      const detPcfc = det.data.pcfcAllocations || [];

      // Open invoices for the customer, with THIS invoice's own
      // discounted amount added back so it can be re-entered.
      let invs = [];
      try {
        const oi = await axios.get(
          `${CONFIG.API}/api/bills/discounting/open-invoices/${r.customerCode}`, auth);
        invs = oi.data;
      } catch (e) { /* ignore */ }

      const addBack = num(r.fcAmountDiscounted);
      const exInv = invs.find(i => String(i.invoiceId) === String(r.invoiceId));
      if (exInv) exInv.fcOpen = num(exInv.fcOpen) + addBack;
      else invs.push({
        invoiceId: r.invoiceId, invoiceNo: r.invoiceNo,
        dueDate: r.dueDate, fcOpen: addBack, currency: r.currency,
        weightedAvgCostRate: r.weightedAvgCostRate
      });
      setOpenInvoices(invs);

      // Forwards available at this bank, plus the ones this entry is
      // already using added back into availability.
      let fwds = [];
      try {
        const url = type === 'INR'
          ? `${CONFIG.API}/api/bills/discounting/available-forwards/${r.invoiceId}`
          : `${CONFIG.API}/api/bills/discounting/available-forwards-fc/${r.invoiceId}/${ddate}`;
        const f = await axios.get(url, { ...auth, params: { bank: r.bank } });
        fwds = readForwards(f.data).list;
      } catch (e) { /* ignore */ }

      detFwd.forEach(d => {
        const ex = fwds.find(f => f.forwardId === d.forwardId);
        if (ex) ex.fcAvailable = num(ex.fcAvailable) + num(d.fcAllocated);
        else fwds.push({
          forwardId: d.forwardId, dealNo: d.dealNo, bank: d.bank,
          forwardRate: d.forwardRate, fcAvailable: num(d.fcAllocated),
          windowStartDate: d.windowStartDate, windowEndDate: d.windowEndDate
        });
      });

      // PCFC, same idea.
      let pcfc = [];
      if (type === 'FC' && r.bank) {
        try {
          const p = await axios.get(
            `${CONFIG.API}/api/bills/discounting/available-pcfc/${r.invoiceId}`,
            { ...auth, params: { bank: r.bank } });
          pcfc = p.data;
        } catch (e) { /* ignore */ }
      }

      detPcfc.forEach(d => {
        const ex = pcfc.find(p => p.pcfcId === d.pcfcId);
        if (ex) ex.fcAvailable = num(ex.fcAvailable) + num(d.fcAllocated);
        else pcfc.push({
          pcfcId: d.pcfcId, pcfcRef: d.pcfcRef, pcfcDate: d.pcfcDate,
          drawdownRate: d.drawdownRate, fcAvailable: num(d.fcAllocated),
          bank: d.bank, currency: d.currency
        });
      });

      setAvailableForwards(fwds);
      setForwardReason(null);
      setAvailablePcfc(pcfc);

      setForwardAllocations(detFwd.map(d => ({
        forwardId: d.forwardId,
        fcAllocated: String(d.fcAllocated),
        forwardRate: d.forwardRate
      })));
      setPcfcAllocations(detPcfc.map(d => ({
        pcfcId: d.pcfcId,
        fcAllocated: String(d.fcAllocated)
      })));

      if (type === 'FC') {
        await loadEefcContext(r.invoiceId, r.bank, ddate);
      } else {
        setEefcContext(null);
      }

      const eefcFc = num(det.data.eefcFc);

      setForm({
        customerCode:       r.customerCode || '',
        invoiceId:          r.invoiceId ? String(r.invoiceId) : '',
        discountingDate:    ddate,
        bank:               r.bank || '',
        fcAmountDiscounted: r.fcAmountDiscounted != null ? String(r.fcAmountDiscounted) : '',
        discountingType:    type,
        spotFc:             num(r.spotFc)   > 0 ? String(r.spotFc)   : '',
        spotRate:           num(r.spotRate) > 0 ? String(r.spotRate) : '',
        pcfcFc:             '', pcfcRate: '',
        drawdownRate:       num(r.drawdownRate) > 0 ? String(r.drawdownRate) : '',
        loanDueDate:        r.loanDueDate ? String(r.loanDueDate).slice(0, 10) : '',
        notes:              typeof r.notes === 'string' ? r.notes : '',
        eefcFc:             eefcFc > 0 ? String(eefcFc) : '',
        eefcAccountId:      det.data.eefcAccountId ? String(det.data.eefcAccountId) : '',
        benchmarkRate:      ''
      });

      setEditingRecord(r);
      setShowForm(true);
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
      alert(serverMessage(err, 'Could not load this record for editing.'));
    }
  };

  // ------------------------------------------------------------------
  // create a missing EEFC ledger without leaving the page
  // ------------------------------------------------------------------
  const handleCreateLedger = async () => {
    if (!eefcContext?.bankId || !eefcContext?.currency) return;
    if (!window.confirm(
      `Create a ${pairLabel(eefcContext.currency)} EEFC ledger at ${form.bank}?\n\n`
    + 'It starts with a zero balance. You can add an opening balance later on the Bank page.'
    )) return;

    try {
      setCreatingLedger(true);
      await axios.post(
        `${CONFIG.API}/api/bills/banks/${eefcContext.bankId}/eefc`,
        {
          currency: eefcContext.currency,
          accountNo: null,
          openingFc: 0,
          openingRate: 0,
          openingDate: null,
          notes: 'Created from the discounting page'
        }, auth);
      await loadEefcContext(form.invoiceId, form.bank, form.discountingDate);
    } catch (err) {
      alert(serverMessage(err, 'Could not create the EEFC ledger.'));
    } finally {
      setCreatingLedger(false);
    }
  };

  // ------------------------------------------------------------------
  // totals
  //
  // Spot + Forward + PCFC + EEFC must equal the FC discounted.
  // The rate is worked out on the CONVERTED part only (rule R11).
  // ------------------------------------------------------------------
  const calcTotals = () => {
    const fcToDiscount = num(form.fcAmountDiscounted);
    const spotFc       = num(form.spotFc);
    const eefcFc       = form.discountingType === 'FC' ? num(form.eefcFc) : 0;

    const forwardFc = forwardAllocations
      .reduce((s, a) => s + num(a.fcAllocated), 0);
    const pcfcFc = pcfcAllocations
      .reduce((s, a) => s + num(a.fcAllocated), 0);

    const convertedFc    = spotFc + forwardFc + pcfcFc;
    const totalAllocated = convertedFc + eefcFc;
    const remaining      = fcToDiscount - totalAllocated;

    let weightedSum = 0;
    if (spotFc > 0 && num(form.spotRate) > 0) {
      weightedSum += spotFc * num(form.spotRate);
    }
    forwardAllocations.forEach(a => {
      const fc = num(a.fcAllocated);
      if (fc > 0) weightedSum += fc * num(a.forwardRate);
    });
    pcfcAllocations.forEach(a => {
      const fc = num(a.fcAllocated);
      if (fc > 0) {
        const loan = availablePcfc.find(p => p.pcfcId === a.pcfcId);
        weightedSum += fc * (loan ? num(loan.drawdownRate) : 0);
      }
    });

    const weightedAvg = convertedFc > 0 ? weightedSum / convertedFc : 0;

    return {
      fcToDiscount, convertedFc, eefcFc, totalAllocated, remaining, weightedAvg
    };
  };

  // ------------------------------------------------------------------
  // save
  // ------------------------------------------------------------------
  const handleSubmit = async () => {
    if (!form.customerCode)       { alert('Please select a customer'); return; }
    if (!form.invoiceId)          { alert('Please select an invoice'); return; }
    if (!form.discountingDate)    { alert('Please enter the discounting date'); return; }
    if (!form.bank)               { alert('Please select the bank'); return; }
    if (!form.fcAmountDiscounted) { alert('Please enter the FC amount to discount'); return; }

    const t = calcTotals();

    // B16 — nothing may be negative.
    if (num(form.fcAmountDiscounted) <= 0) {
      alert('The FC amount to discount must be greater than zero.'); return;
    }
    if (num(form.spotFc) < 0 || num(form.eefcFc) < 0) {
      alert('Amounts cannot be negative.'); return;
    }
    if (forwardAllocations.some(a => num(a.fcAllocated) < 0) ||
        pcfcAllocations.some(a => num(a.fcAllocated) < 0)) {
      alert('Allocations cannot be negative.'); return;
    }

    if (num(form.spotFc) > 0 && num(form.spotRate) <= 0) {
      alert('Please enter the spot rate'); return;
    }

    if (form.discountingType === 'FC') {
      if (!form.loanDueDate) { alert('Please enter the loan due date'); return; }
      if (t.eefcFc > 0 && !form.eefcAccountId) {
        alert('Please choose the EEFC ledger the money is being kept in.'); return;
      }
    }

    if (Math.abs(t.remaining) > 0.01) {
      alert(
        'The allocation does not balance.\n\n'
      + `Spot, forward, PCFC and EEFC come to ${fmt(t.totalAllocated)}, but `
      + `${fmt(t.fcToDiscount)} is being discounted.\n\n`
      + `Remaining to allocate: ${fmt(t.remaining)}`);
      return;
    }

    const payload = {
      invoiceId:          parseInt(form.invoiceId),
      discountingDate:    form.discountingDate,
      bank:               form.bank,
      fcAmountDiscounted: num(form.fcAmountDiscounted),
      discountingType:    form.discountingType,
      spotFc:             form.spotFc      ? num(form.spotFc)      : null,
      spotRate:           form.spotRate    ? num(form.spotRate)    : null,
      pcfcFc:             form.pcfcFc      ? num(form.pcfcFc)      : null,
      pcfcRate:           form.pcfcRate    ? num(form.pcfcRate)    : null,
      drawdownRate:       form.drawdownRate ? num(form.drawdownRate) : null,
      loanDueDate:        form.loanDueDate  || null,
      notes:              form.notes        || null,

      eefcFc: form.discountingType === 'FC' && t.eefcFc > 0 ? t.eefcFc : null,
      eefcAccountId: form.discountingType === 'FC' && t.eefcFc > 0 && form.eefcAccountId
        ? parseInt(form.eefcAccountId) : null,
      benchmarkRate: form.benchmarkRate ? num(form.benchmarkRate) : null,

      forwardAllocations: forwardAllocations
        .filter(a => num(a.fcAllocated) > 0)
        .map(a => ({ forwardId: a.forwardId, fcAllocated: num(a.fcAllocated) })),

      pcfcAllocations: form.discountingType === 'FC'
        ? pcfcAllocations
            .filter(a => num(a.fcAllocated) > 0)
            .map(a => ({ pcfcId: a.pcfcId, fcAllocated: num(a.fcAllocated) }))
        : []
    };

    try {
      setSaving(true);
      if (editingRecord) {
        await axios.put(
          `${CONFIG.API}/api/bills/discounting/${editingRecord.discountingId}`,
          payload, auth);
      } else {
        await axios.post(`${CONFIG.API}/api/bills/discounting`, payload, auth);
      }
      resetForm();
      loadRecords();
      loadCustomers();
    } catch (err) {
      alert(serverMessage(err, 'Could not save the discounting record.'));
    } finally {
      setSaving(false);
    }
  };

  // ------------------------------------------------------------------
  // derived
  // ------------------------------------------------------------------
  const getStatusBadge = (status) =>
    status === 'REALISED' ? 'badge-done' : 'badge-open';

  const selectedInvoice = openInvoices.find(
    i => String(i.invoiceId) === String(form.invoiceId));

  const invoiceCurrency = selectedInvoice?.currency || eefcContext?.currency || '';

  const { fcToDiscount, convertedFc, eefcFc, totalAllocated, remaining, weightedAvg }
    = calcTotals();

  const costRate = num(selectedInvoice?.weightedAvgCostRate) || num(eefcContext?.costRate);

  const filteredRecords = records.filter(r => {
    const matchStatus   = filterStatus   === '' || r.realisationStatus === filterStatus;
    const matchCustomer = filterCustomer === '' ||
      String(r.customerCode || '').toLowerCase().includes(filterCustomer.toLowerCase());
    return matchStatus && matchCustomer;
  });

  const totalParked = records.reduce((s, r) => s + num(r.eefcFc), 0);

  const bankOptions = () => {
    const opts = [];
    if (form.bank && !banks.some(b => b.bankName === form.bank)) {
      opts.push(
        <option key="__old" value={form.bank}>{form.bank} (old)</option>);
    }
    banks.forEach(b => opts.push(
      <option key={b.bankId} value={b.bankName}>
        {b.bankName}{b.branch ? ' — ' + b.branch : ''}
      </option>));
    return opts;
  };

  const balanced = Math.abs(remaining) < 0.01;

  // ------------------------------------------------------------------
  // the balance bar, shared by both types
  // ------------------------------------------------------------------
  const BalanceBar = () => {
    if (!(fcToDiscount > 0)) return null;
    return (
      <div style={{
        background: balanced ? '#F0FDF4' : '#FEF2F2',
        border: `1px solid ${balanced ? '#BBF7D0' : '#FCA5A5'}`,
        borderRadius: '6px', padding: '10px 14px',
        fontSize: '13px', marginTop: '8px'
      }}>
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <span><strong>FC to discount:</strong> {fmt(fcToDiscount)}</span>
          <span><strong>Total allocated:</strong> {fmt(totalAllocated)}</span>
          <span style={{
            color: balanced ? '#166534' : '#DC2626', fontWeight: '700'
          }}>
            <strong>Remaining:</strong> {fmt(remaining)}
          </span>
          {eefcFc > 0 && (
            <span style={{ color: '#7C3AED', fontWeight: '600' }}>
              <strong>Kept in EEFC:</strong> {fmt(eefcFc)} {ccyCode(invoiceCurrency)}
            </span>
          )}
          {weightedAvg > 0 && (
            <span style={{ color: '#1D4ED8', fontWeight: '600' }}>
              <strong>Wtd avg rate{eefcFc > 0 ? ' (converted part)' : ''}:</strong>{' '}
              {weightedAvg.toFixed(4)}
            </span>
          )}
          {weightedAvg > 0 && costRate > 0 && (
            <span style={{
              color: weightedAvg >= costRate ? '#166534' : '#DC2626',
              fontWeight: '600'
            }}>
              <strong>Ex diff:</strong> ₹{fmt0((weightedAvg - costRate) * convertedFc)}
            </span>
          )}
        </div>
        {eefcFc > 0 && (
          <div style={{ ...noteStyle, marginTop: '6px' }}>
            The rate and the exchange difference cover the {fmt(convertedFc)} that was
            actually converted. The {fmt(eefcFc)} kept in EEFC has no rate yet — its
            profit or loss appears when it leaves the ledger.
          </div>
        )}
      </div>
    );
  };

  // ------------------------------------------------------------------
  // the forward table, shared by both types
  // ------------------------------------------------------------------
  const ForwardTable = ({ dateLabel }) => (
    <div style={{ marginBottom: '10px' }}>
      <div style={{ fontSize: '12px', fontWeight: '600',
                    color: '#374151', marginBottom: '6px' }}>
        Forward contracts at {form.bank || 'this bank'} matching the {dateLabel}
      </div>

      {availableForwards.length === 0 ? (
        <div style={warnBox}>
          {forwardReason || 'No forward contracts match.'}
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Deal no</th>
              <th>Bank</th>
              <th>Forward rate</th>
              <th>FC available</th>
              <th>Window</th>
              <th>Allocate FC</th>
              <th>INR value</th>
            </tr>
          </thead>
          <tbody>
            {availableForwards.map(fwd => {
              const alloc   = forwardAllocations.find(a => a.forwardId === fwd.forwardId);
              const allocFc = num(alloc?.fcAllocated);
              const inrVal  = allocFc * num(fwd.forwardRate);
              return (
                <tr key={fwd.forwardId}>
                  <td>{fwd.dealNo}</td>
                  <td>{fwd.bank}</td>
                  <td>{Number(fwd.forwardRate).toFixed(4)}</td>
                  <td>{fmt(fwd.fcAvailable)}</td>
                  <td style={{ fontSize: '11px' }}>
                    {fwd.windowStartDate} to {fwd.windowEndDate}
                  </td>
                  <td>
                    <input
                      type="number" min="0"
                      value={alloc?.fcAllocated || ''}
                      onChange={e => handleForwardAllocationChange(
                        fwd.forwardId, e.target.value)}
                      placeholder="0"
                      max={fwd.fcAvailable}
                      style={smallInput} />
                  </td>
                  <td style={{ color: '#166534', fontWeight: '600' }}>
                    {allocFc > 0 ? fmt0(inrVal) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  // ------------------------------------------------------------------
  // the spot pair of boxes, shared by both types
  // ------------------------------------------------------------------
  const SpotFields = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                    gap: '12px', marginBottom: '6px' }}>
        <div className="form-field">
          <label>Spot — FC amount</label>
          <input type="number" min="0" name="spotFc" value={form.spotFc}
            onChange={handleChange} placeholder="0" />
        </div>
        <div className="form-field">
          <label>Spot rate</label>
          <input type="number" min="0" name="spotRate" value={form.spotRate}
            onChange={handleChange} placeholder="e.g. 84.50" />
        </div>
      </div>
      {form.spotFc && form.spotRate && (
        <div style={{ ...noteStyle, marginBottom: '8px', paddingLeft: '4px' }}>
          Spot INR = {fmt0(num(form.spotFc) * num(form.spotRate))}
        </div>
      )}
    </>
  );

  return (
    <div className="content">

      {/* ============================================================ */}
      {/* Edit warning                                                 */}
      {/* ============================================================ */}
      {showEditWarning && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff', borderRadius: '10px', padding: '28px',
            maxWidth: '480px', width: '90%',
            boxShadow: '0 8px 32px rgba(0,0,0,0.18)'
          }}>
            <div style={{ fontSize: '18px', fontWeight: '700',
                          color: '#1E293B', marginBottom: '12px' }}>
              ⚠️ Edit this discounting
            </div>
            <div style={{ fontSize: '14px', color: '#475569',
                          lineHeight: '1.6', marginBottom: '20px' }}>
              Saving rebuilds the entry from scratch: the forwards it used are
              released, any PCFC repayment is put back, the bill balance is
              restored, and the new figures are applied in their place. It all
              happens together, or not at all.
              <div style={{ marginTop: '10px' }}>
                It will be refused if:
                <ul style={{ marginTop: '6px', paddingLeft: '18px' }}>
                  <li>the bill has already been realised</li>
                  <li>money this entry parked in EEFC has already been used</li>
                </ul>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
              <button className="btn-secondary"
                onClick={() => { setShowEditWarning(false); setPendingEditRecord(null); }}>
                Close
              </button>
              <button className="btn-primary" onClick={handleEditConfirm}>
                Yes, edit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* Summary cards                                                */}
      {/* ============================================================ */}
      <div className="metric-grid">
        <div className="metric-card">
          <div className="metric-label">Total discounting</div>
          <div className="metric-value val-blue">{records.length}</div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Open</div>
          <div className="metric-value val-amber">
            {records.filter(r => r.realisationStatus === 'OPEN').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Realised</div>
          <div className="metric-value val-green">
            {records.filter(r => r.realisationStatus === 'REALISED').length}
          </div>
        </div>
        <div className="metric-card">
          <div className="metric-label">Parked in EEFC (FC)</div>
          <div className="metric-value val-blue">
            {totalParked > 0 ? fmt0(totalParked) : '—'}
          </div>
        </div>
      </div>

      {/* ============================================================ */}
      {/* Header                                                       */}
      {/* ============================================================ */}
      <div className="section-header">
        <div className="section-title">Discounting records</div>
        <button className="btn-primary" onClick={() => {
          if (showForm) { resetForm(); } else { setForm(emptyForm); setShowForm(true); }
        }}>
          {showForm ? '✕ Close' : '+ New discounting'}
        </button>
      </div>

      {/* ============================================================ */}
      {/* Form                                                         */}
      {/* ============================================================ */}
      {showForm && (
        <div className="form-card" style={{ maxWidth: '860px' }}>
          <div className="form-step" style={{ marginBottom: '16px' }}>
            {editingRecord ? '✏️ Edit discounting record' : '📋 New discounting entry'}
          </div>

          {/* Row 1 — Customer + Invoice */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Customer code</label>
              <select name="customerCode" value={form.customerCode}
                onChange={handleChange} disabled={!!editingRecord}>
                <option value="">— Select customer —</option>
                {customers.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <div className="form-field">
              <label>Invoice</label>
              <select name="invoiceId" value={form.invoiceId}
                onChange={handleChange} disabled={!!editingRecord}>
                <option value="">— Select invoice —</option>
                {openInvoices.map(inv => (
                  <option key={inv.invoiceId} value={inv.invoiceId}>
                    {inv.invoiceNo} — {ccyCode(inv.currency)} {fmt0(inv.fcOpen)} open — due {inv.dueDate}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2 — Date, Bank, FC amount, Type */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr',
                        gap: '12px', marginBottom: '12px' }}>
            <div className="form-field">
              <label>Discounting date</label>
              <input type="date" name="discountingDate"
                value={form.discountingDate} onChange={handleChange} />
            </div>
            <div className="form-field">
              <label>Bank</label>
              <select name="bank" value={form.bank} onChange={handleChange}>
                <option value="">— Select bank —</option>
                {bankOptions()}
              </select>
            </div>
            <div className="form-field">
              <label>FC amount to discount</label>
              <input type="number" min="0" name="fcAmountDiscounted"
                value={form.fcAmountDiscounted} onChange={handleChange}
                placeholder="0" max={selectedInvoice?.fcOpen || ''} />
            </div>
            <div className="form-field">
              <label>Discounting type</label>
              <select name="discountingType" value={form.discountingType}
                onChange={handleChange}>
                <option value="INR">INR</option>
                <option value="FC">FC (EBR)</option>
              </select>
            </div>
          </div>

          <div style={{ ...noteStyle, marginBottom: '14px' }}>
            Forward contracts and PCFC loans are matched to the bank you pick here.
            Changing the bank reloads both lists.
          </div>

          {/* Invoice details bar */}
          {selectedInvoice && (
            <div style={{ ...infoBox, display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
              <span><strong>Currency:</strong> {pairLabel(selectedInvoice.currency)}</span>
              <span><strong>FC open:</strong> {fmt(selectedInvoice.fcOpen)}</span>
              <span><strong>Due date:</strong> {selectedInvoice.dueDate}</span>
              <span><strong>Cost rate:</strong> {Number(selectedInvoice.weightedAvgCostRate || 0).toFixed(4)}</span>
            </div>
          )}

          {/* B14 — an invoice with no currency, said plainly */}
          {selectedInvoice && !selectedInvoice.currency && (
            <div style={warnBox}>
              This invoice has no currency recorded, so no forward contract, PCFC loan or
              EEFC ledger can be matched to it. Open the Invoices page and set its currency
              first.
            </div>
          )}

          {/* ======================================================== */}
          {/* INR discounting                                          */}
          {/* ======================================================== */}
          {form.discountingType === 'INR' && form.invoiceId && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ fontSize: '13px', fontWeight: '600',
                            color: '#374151', marginBottom: '10px' }}>
                Allocation — Spot / Forward
              </div>

              <SpotFields />
              <ForwardTable dateLabel="invoice due date" />
              <BalanceBar />
            </div>
          )}

          {/* ======================================================== */}
          {/* FC (EBR) discounting                                     */}
          {/* ======================================================== */}
          {form.discountingType === 'FC' && form.invoiceId && (
            <div style={{ marginBottom: '16px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                            gap: '12px', marginBottom: '12px' }}>
                <div className="form-field">
                  <label>Loan due date</label>
                  <input type="date" name="loanDueDate"
                    value={form.loanDueDate} onChange={handleChange} />
                </div>
              </div>

              <div style={{ fontSize: '13px', fontWeight: '600',
                            color: '#374151', marginBottom: '10px' }}>
                Allocation — Spot / Forward / PCFC / EEFC
              </div>

              <SpotFields />
              <ForwardTable dateLabel="discounting date" />

              {/* ---- PCFC repayment ---- */}
              <div style={{ marginTop: '14px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600',
                              color: '#374151', marginBottom: '8px' }}>
                  PCFC repayment (optional) — oldest loans first
                </div>

                {!form.bank && (
                  <div style={warnBox}>
                    Select a bank above to see its PCFC loans.
                  </div>
                )}

                {form.bank && availablePcfc.length === 0 && (
                  <div style={warnBox}>
                    No open PCFC loans at {form.bank} in {pairLabel(invoiceCurrency)}.
                  </div>
                )}

                {availablePcfc.length > 0 && (
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>PCFC ref</th>
                        <th>Date</th>
                        <th>Draw rate</th>
                        <th>FC outstanding</th>
                        <th>Repay FC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {availablePcfc.map(p => {
                        const alloc = pcfcAllocations.find(a => a.pcfcId === p.pcfcId);
                        return (
                          <tr key={p.pcfcId}>
                            <td>{p.pcfcRef}</td>
                            <td>{p.pcfcDate}</td>
                            <td>{Number(p.drawdownRate).toFixed(4)}</td>
                            <td>{fmt(p.fcAvailable)}</td>
                            <td>
                              <input
                                type="number" min="0"
                                value={alloc?.fcAllocated || ''}
                                onChange={e => handlePcfcAllocationChange(
                                  p.pcfcId, e.target.value)}
                                placeholder="0"
                                max={p.fcAvailable}
                                style={smallInput} />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>

              {/* ---- Keep in EEFC ---- */}
              <div style={{ marginTop: '18px' }}>
                <div style={{ fontSize: '13px', fontWeight: '600',
                              color: '#374151', marginBottom: '4px' }}>
                  Keep in EEFC (optional)
                </div>
                <div style={{ ...noteStyle, marginBottom: '8px' }}>
                  Foreign currency you are not converting today. It is parked as a parcel
                  that remembers this bill and its cost rate, and is converted later from
                  the EEFC ledger when the rate suits you.
                </div>

                {!form.bank && (
                  <div style={warnBox}>
                    Select a bank above — an EEFC ledger belongs to one bank and one currency.
                  </div>
                )}

                {form.bank && eefcContext && eefcContext.accounts?.length === 0 && (
                  <div style={warnBox}>
                    {form.bank} has no {pairLabel(eefcContext.currency)} EEFC ledger yet.
                    {eefcContext.bankId ? (
                      <div style={{ marginTop: '8px' }}>
                        <button className="btn-secondary"
                          onClick={handleCreateLedger} disabled={creatingLedger}>
                          {creatingLedger
                            ? 'Creating...'
                            : `+ Create the ${pairLabel(eefcContext.currency)} ledger at ${form.bank}`}
                        </button>
                      </div>
                    ) : (
                      <div style={{ marginTop: '6px' }}>
                        {eefcContext.bankMatches > 1
                          ? 'More than one bank has this name, so the ledger has to be created '
                          + 'on the Bank page against the right branch.'
                          : 'Add it on the Bank page first.'}
                      </div>
                    )}
                  </div>
                )}

                {form.bank && eefcContext && eefcContext.accounts?.length > 0 && (
                  <>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                  gap: '12px', marginBottom: '6px' }}>
                      <div className="form-field">
                        <label>EEFC — FC amount to park</label>
                        <input type="number" min="0" name="eefcFc" value={form.eefcFc}
                          onChange={handleChange} placeholder="0" />
                      </div>
                      <div className="form-field">
                        <label>EEFC ledger</label>
                        <select name="eefcAccountId" value={form.eefcAccountId}
                          onChange={handleChange}>
                          <option value="">— Select ledger —</option>
                          {eefcContext.accounts.map(a => (
                            <option key={a.accountId} value={a.accountId}>
                              {a.bankName} — {pairLabel(a.currency)}
                              {a.accountNo ? ` (${a.accountNo})` : ''} — balance {fmt(a.balanceFc)}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    {num(form.eefcFc) > 0 && (
                      <div style={{
                        ...infoBox, marginTop: '8px', marginBottom: '4px'
                      }}>
                        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                          <span><strong>Cost rate of the parcel:</strong> {costRate ? costRate.toFixed(4) : '—'}</span>
                          <span>
                            <strong>Market rate on arrival:</strong>{' '}
                            {num(form.spotFc) > 0 && num(form.spotRate) > 0
                              ? `${num(form.spotRate).toFixed(4)} (your spot rate today)`
                              : num(eefcContext.benchmarkRate) > 0
                                ? `${num(eefcContext.benchmarkRate).toFixed(4)} (close of ${eefcContext.benchmarkDate})`
                                : 'not available'}
                          </span>
                        </div>
                        <div style={{ ...noteStyle, marginTop: '6px' }}>
                          These two rates are what the profit is measured against later —
                          what the export was priced at, and what the market was worth the
                          day the money arrived.
                        </div>
                      </div>
                    )}

                    {/* Only needed when rate_history has nothing for that day */}
                    {num(form.eefcFc) > 0 &&
                     !(num(form.spotFc) > 0 && num(form.spotRate) > 0) &&
                     num(eefcContext.benchmarkRate) === 0 && (
                      <>
                        <div style={warnBox}>
                          There is no market rate for {pairLabel(eefcContext.currency)} on or
                          before {form.discountingDate || 'this date'}. Either upload it on the
                          Rates page, or type it below.
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr',
                                      gap: '12px', marginBottom: '6px' }}>
                          <div className="form-field">
                            <label>Market rate on the discounting date</label>
                            <input type="number" min="0" name="benchmarkRate"
                              value={form.benchmarkRate} onChange={handleChange}
                              placeholder="e.g. 84.20" />
                          </div>
                        </div>
                      </>
                    )}
                  </>
                )}
              </div>

              <BalanceBar />
            </div>
          )}

          {/* Notes */}
          <div style={{ marginBottom: '16px' }}>
            <div className="form-field">
              <label>Notes (optional)</label>
              <textarea name="notes" value={form.notes}
                onChange={handleChange} rows={2}
                placeholder="Any additional notes..."
                style={{
                  width: '100%', padding: '8px 10px',
                  border: '1px solid #CBD5E1', borderRadius: '6px',
                  fontSize: '14px', resize: 'vertical',
                  fontFamily: 'inherit', boxSizing: 'border-box'
                }} />
            </div>
          </div>

          {/* Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
            <button className="btn-secondary" onClick={resetForm}>Cancel</button>
            <button className="btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving
                ? 'Saving...'
                : (editingRecord ? '✓ Update record' : '✓ Save discounting')}
            </button>
          </div>
        </div>
      )}

      {/* ============================================================ */}
      {/* Filter bar                                                   */}
      {/* ============================================================ */}
      <div style={{
        display: 'flex', gap: '12px', alignItems: 'center',
        marginBottom: '12px', flexWrap: 'wrap'
      }}>
        <input
          type="text"
          placeholder="🔍 Search customer..."
          value={filterCustomer}
          onChange={e => setFilterCustomer(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', width: '200px'
          }} />
        <select
          value={filterStatus}
          onChange={e => setFilterStatus(e.target.value)}
          style={{
            padding: '7px 12px', border: '1px solid #CBD5E1',
            borderRadius: '6px', fontSize: '14px', color: '#374151'
          }}>
          <option value="">All statuses</option>
          <option value="OPEN">Open</option>
          <option value="REALISED">Realised</option>
        </select>
        {(filterStatus !== '' || filterCustomer !== '') && (
          <button
            onClick={() => { setFilterStatus(''); setFilterCustomer(''); }}
            style={{
              padding: '7px 12px', border: '1px solid #CBD5E1',
              borderRadius: '6px', fontSize: '13px', cursor: 'pointer',
              background: '#F1F5F9', color: '#64748B'
            }}>
            ✕ Clear filters
          </button>
        )}
        <span style={{ fontSize: '13px', color: '#64748B', marginLeft: 'auto' }}>
          Showing {filteredRecords.length} of {records.length} records
        </span>
      </div>

      {/* ============================================================ */}
      {/* Records table                                                */}
      {/* ============================================================ */}
      <div className="table-wrap">
        {loading ? (
          <p style={{ padding: '20px', color: '#64748B' }}>Loading...</p>
        ) : filteredRecords.length === 0 ? (
          <p style={{ padding: '20px', color: '#64748B' }}>
            No discounting records found.
          </p>
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice no</th>
                <th>Customer</th>
                <th>Currency</th>
                <th>Disc date</th>
                <th>Bank</th>
                <th>FC discounted</th>
                <th>In EEFC</th>
                <th>Type</th>
                <th>Wtd avg rate</th>
                <th>Cost rate</th>
                <th>Ex diff ₹</th>
                <th>Due date</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.map(r => (
                <tr key={r.discountingId}>
                  <td>{r.invoiceNo}</td>
                  <td>{r.customerCode}</td>
                  <td>{ccyCode(r.currency)}</td>
                  <td>{String(r.discountingDate || '').slice(0, 10)}</td>
                  <td>{r.bank}</td>
                  <td>{fmt0(r.fcAmountDiscounted)}</td>
                  <td style={{ color: num(r.eefcFc) > 0 ? '#7C3AED' : '#94A3B8',
                               fontWeight: num(r.eefcFc) > 0 ? '600' : '400' }}>
                    {num(r.eefcFc) > 0 ? fmt0(r.eefcFc) : '—'}
                  </td>
                  <td>{r.discountingType}</td>
                  <td>{num(r.weightedAvgRate) > 0
                    ? Number(r.weightedAvgRate).toFixed(4) : '—'}</td>
                  <td>{num(r.weightedAvgCostRate) > 0
                    ? Number(r.weightedAvgCostRate).toFixed(4) : '—'}</td>
                  <td style={{
                    color: num(r.exchangeDiff) >= 0 ? '#166534' : '#DC2626',
                    fontWeight: '600'
                  }}>
                    {num(r.weightedAvgRate) > 0 ? fmt0(r.exchangeDiff) : '—'}
                  </td>
                  <td>{r.dueDate}</td>
                  <td>
                    <span className={`badge ${getStatusBadge(r.realisationStatus)}`}>
                      {r.realisationStatus}
                    </span>
                  </td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {r.realisationStatus === 'OPEN' ? (
                      <button className="btn-secondary"
                        onClick={() => handleEditClick(r)}>
                        Edit
                      </button>
                    ) : (
                      <span
                        title="This bill is realised. Delete the realisation first to edit the discounting."
                        style={{ fontSize: '12px', color: '#92400E' }}>
                        🔒 Realised
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {num(totalParked) > 0 && (
        <div style={{ ...noteStyle, marginTop: '10px' }}>
          The "In EEFC" column is money parked, not converted. Its profit or loss shows on
          the EEFC ledger on the Bank page, on the day it finally leaves.
        </div>
      )}

    </div>
  );
}

export default DiscountingPage;